import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/lib/apiClient';
import { useStaffAuth } from '@/features/auth/useStaffAuth';
import { inventoryErrorKey } from '@/features/inventory/inventoryErrors';
// A pure formatter, not the register's furniture — `formatInstant` renders an
// ISO instant in the reader's own language and zone and has no styling in it.
import { INPUT, SECONDARY_BUTTON, formatInstant } from '@/features/inventory/registerUi';
import { useGoLive, useRunChecks, useUnGoLive, type CheckResult } from './useProvisioning';

/**
 * THE GO-LIVE SURFACE. ONE COMPONENT, THREE ENTRANCES (slice 18f).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS FILE EXISTS. Mahad, 18f: *"No I want the natural way, i want to go
 * live, so do the work."*
 * ─────────────────────────────────────────────────────────────────────────────
 * Go-live used to exist ONLY as step 4 of the provisioning wizard. Close that
 * wizard — or provision a vehicle last week — and there was no way back to the
 * one control that decides whether a customer can see their own truck. The
 * vehicle sat provisioned, reporting and invisible, with no screen able to
 * release it.
 *
 * So the panel is now reachable from the wizard's last step, from the
 * `/vehicles` row detail, and from the selected vehicle on `/live`. All three
 * render THIS component, because three copies of a gate is three chances for one
 * of them to be wrong about what "ready" means.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 THE DISABLED "GO LIVE" BUTTON IS DELIBERATE AND IS **NOT** AN
 *    "ABSENCE OVER DISABLED" VIOLATION. DO NOT DELETE IT.
 * ─────────────────────────────────────────────────────────────────────────────
 * CLAUDE.md's rule governs **features that do not exist**: a greyed Billing tab
 * promises a product and dates the promise. Go-live is a feature that EXISTS and
 * has ONE UNMET PRECONDITION, and the failing checks printed directly above the
 * button are the explanation a hidden control could never give. Hide it and the
 * operator learns nothing except that the screen is missing something. The
 * provisioning wizard has always done exactly this, for exactly this reason.
 *
 * The ALREADY-LIVE case gets absence OF THE GO-LIVE BUTTON: that action is done,
 * not blocked, so there is nothing to explain. It is not an empty screen — see
 * the three states below.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * 🔴 THREE STATES, NOT TWO (slice 18g). ONE COMPONENT STILL OWNS ALL OF THEM.
 * ──────────────────────────────────────────────────────────────────────────────
 *   NEVER LIVE   `liveAt === null`                  → checks + Go live
 *   LIVE         `liveAt !== null && hiddenAt === null` → the date + Hide
 *   HIDDEN       `liveAt !== null && hiddenAt !== null` → withdrawn banner,
 *                                                        checks + Show again
 *
 * 🔴 `liveAt` ALONE IS NO LONGER THE ANSWER, and reading it as one is the
 * defect this state table exists to prevent. A withdrawn vehicle still carries
 * the `liveAt` it was FIRST cleared at — the column is history and is
 * deliberately never rewritten — so a panel keyed on `liveAt !== null` would tell
 * an operator "Live since 25 Aug" about a vehicle the customer cannot see. The
 * visibility predicate is both columns, and it is the same one the server exports
 * once as `IS_CUSTOMER_VISIBLE`.
 *
 * The HIDDEN state re-uses the checks flow rather than offering a bare button,
 * because `POST /go-live` re-runs the checklist either way: re-showing a vehicle
 * whose tracker was taken out for maintenance is refused by `tracker_fitted`
 * until a tracker is fitted again, and the operator should read that reason here
 * rather than meet it as a 409.
 *
 * 🔴 HIDING IS CONFIRMED INLINE, NOT IN A NESTED MODAL. This panel renders
 * inside the `/vehicles` row-detail Dialog and inside the wizard's step 4; a
 * second modal on top of either is a stacking and focus problem for no gain. The
 * inline block states the consequence in words and cannot be dismissed by
 * clicking away, which is what the confirmation is for.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 THE SERVER'S VERDICT OVERRIDES THE SCREEN'S.
 * ─────────────────────────────────────────────────────────────────────────────
 * `POST /go-live` re-runs every check itself and answers `409 checks_failed`
 * with the failing ones. Three green ticks fetched a minute ago are not
 * evidence — the tracker may have gone quiet since. When that 409 arrives its
 * checks REPLACE what is on screen and the button re-disables, so the operator
 * sees the reason rather than a button that quietly did nothing.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 GO LIVE IS SUPER-ADMIN ONLY; RUNNING THE CHECKS IS NOT.
 * ─────────────────────────────────────────────────────────────────────────────
 * `POST /go-live` is `requireSuperAdmin` — it decides what a paying customer
 * sees — while `GET /checks` is `requireStaff`. So an `admin_staff` gets the
 * checks and NO BUTTON: a control that 403s on click is the dangling control
 * this codebase rules out. One muted line says who can do it, because a support
 * hire finding nothing where a colleague sees a button would otherwise report a
 * broken screen.
 */

/** One check, with its DETAIL — "No position has ever arrived" tells an
 * installer what to do next; a red cross does not. */
export function CheckRow({ check }: { check: CheckResult }) {
  const { t } = useTranslation();
  return (
    <li className="flex items-start gap-3 border-b border-raad-border py-3 last:border-b-0">
      <span
        aria-hidden
        className={[
          'mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[12px] font-bold',
          check.passed
            ? 'bg-raad-primary/15 text-raad-accent'
            : 'bg-raad-error/15 text-raad-error',
        ].join(' ')}
      >
        {check.passed ? '✓' : '!'}
      </span>
      <span className="min-w-0">
        <span className="block text-[13px] font-medium">{t(`provision.checks.${check.id}`)}</span>
        <span className="block text-[12px] text-raad-muted">{check.detail}</span>
      </span>
      <span className="sr-only">{check.passed ? t('provision.passed') : t('provision.failed')}</span>
    </li>
  );
}

function failingChecksFrom(err: unknown): CheckResult[] | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body;
  if (body === null || typeof body !== 'object') return null;
  const b = body as { error?: unknown; checks?: unknown };
  if (b.error !== 'checks_failed' || !Array.isArray(b.checks)) return null;
  // Shape-checked rather than cast: an unexpected body must not throw during
  // render and take the portal down (slice 24b).
  const checks = b.checks.filter(
    (c): c is CheckResult =>
      c !== null &&
      typeof c === 'object' &&
      typeof (c as CheckResult).id === 'string' &&
      typeof (c as CheckResult).detail === 'string',
  );
  return checks.length > 0 ? checks : null;
}

export function GoLivePanel({
  vehicleId,
  liveAt,
  hiddenAt = null,
  autoRun = false,
}: {
  vehicleId: string;
  /**
   * When the customer FIRST saw this vehicle, or null if never. 🔴 NOT the
   * visibility answer on its own — pair it with `hiddenAt`.
   */
  liveAt: string | null;
  /**
   * 18g — non-null = cleared once and since WITHDRAWN. Defaults to null so the
   * two callers that predate this slice keep their exact behaviour; a caller that
   * cannot supply it simply never renders the hidden state, and never renders a
   * WRONG state, because `liveAt !== null && hiddenAt === null` is then the live
   * case it was already showing.
   */
  hiddenAt?: string | null;
  /** The wizard runs the checks the moment step 4 opens. The list screens wait
   * to be asked, so opening a row detail does not fire a Traccar read. */
  autoRun?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { state } = useStaffAuth();
  const isSuperAdmin = state.status === 'authenticated' && state.user.role === 'super_admin';
  const checks = useRunChecks();
  const goLive = useGoLive();
  const hide = useUnGoLive();
  const [serverChecks, setServerChecks] = useState<CheckResult[] | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  /** The inline confirm for hiding. Never a nested modal — see the header. */
  const [confirmingHide, setConfirmingHide] = useState(false);
  const [hideNote, setHideNote] = useState('');
  /**
   * 🔴 WHAT THIS SCREEN HAS DONE SINCE IT MOUNTED, which is not the same as what
   * the props say. The props come from a list that is refetched asynchronously,
   * so after a successful hide the panel would flip back to "Live since …" for as
   * long as the invalidation takes to land — the panel contradicting the action
   * the operator just watched succeed. The override is the local truth until the
   * list catches up; both branches set it, so it can never be half-applied.
   */
  const [override, setOverride] = useState<{ liveAt: string | null; hiddenAt: string | null } | null>(
    null,
  );
  /** The one-line confirmation of what just happened HERE. Absent on arrival. */
  const [flash, setFlash] = useState<'nowLive' | 'nowVisibleAgain' | 'nowHidden' | 'alreadyHidden' | null>(
    null,
  );

  // One shot, keyed on the vehicle — StrictMode double-mounts in development and
  // a bare effect would fire two Traccar reads for every panel opened.
  const autoRunFor = useRef<string | null>(null);
  useEffect(() => {
    if (!autoRun || liveAt !== null || autoRunFor.current === vehicleId) return;
    autoRunFor.current = vehicleId;
    checks.mutate(vehicleId);
    // `checks` is a stable mutation object; adding it re-runs on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRun, liveAt, vehicleId]);

  // The effective state: what this screen has done wins over what it was handed.
  const eff = override ?? { liveAt, hiddenAt };
  const isLive = eff.liveAt !== null && eff.hiddenAt === null;
  const isHidden = eff.liveAt !== null && eff.hiddenAt !== null;

  const flashNote =
    flash === null ? null : (
      <p
        className={[
          'rounded-lg border p-3 text-[12.5px]',
          flash === 'nowHidden' || flash === 'alreadyHidden'
            ? 'border-raad-warn/40 bg-raad-warn/10'
            : 'border-raad-primary/40 bg-raad-primary/10',
        ].join(' ')}
      >
        {/* `provision.nowLive` is the wizard's existing sentence and is reused
            verbatim; the other three are this slice's. */}
        {flash === 'nowLive' ? t('provision.nowLive') : t(`golive.${flash}`)}
      </p>
    );

  async function pressHide() {
    setErrorKey(null);
    try {
      const res = await hide.mutateAsync({
        vehicleId,
        body: { ...(hideNote.trim() ? { reason: hideNote.trim() } : {}) },
      });
      setOverride({ liveAt: eff.liveAt, hiddenAt: res.hiddenAt });
      // 🔴 `changed: false` IS NOT AN ERROR. The server answers 200 with the
      // ORIGINAL instant when it was already hidden; saying "already hidden" is
      // the honest sentence and an error banner would be a lie about a 200.
      setFlash(res.changed ? 'nowHidden' : 'alreadyHidden');
      setConfirmingHide(false);
      setHideNote('');
    } catch (e) {
      setErrorKey(inventoryErrorKey(e));
    }
  }

  // ── LIVE. The customer can see it. Go-live is DONE (no button for it); the
  //    way back is the one control here. ────────────────────────────────
  if (isLive) {
    return (
      <div className="grid gap-2">
        {flashNote}
        <p className="text-[12.5px] text-raad-muted">
          {t('golive.liveSince', { when: formatInstant(eff.liveAt as string, i18n.language) })}
        </p>

        {errorKey !== null ? (
          <p
            role="alert"
            className="rounded-lg border border-raad-error/40 bg-raad-error/10 p-3 text-[12.5px] text-raad-text"
          >
            {t(errorKey)}
          </p>
        ) : null}

        {/* ABSENT for an `admin_staff`, whose click could only ever be a 403 —
            `POST /un-go-live` is `requireSuperAdmin`, exactly like go-live. */}
        {!isSuperAdmin ? (
          <p className="text-[12px] text-raad-muted">{t('golive.visibilitySuperAdminOnly')}</p>
        ) : confirmingHide ? (
          // 🔴 THE CONSEQUENCE IN WORDS, BEFORE the press that causes it.
          <div className="grid gap-3 rounded-xl border border-raad-warn/40 bg-raad-warn/10 p-3">
            <p className="text-[12.5px]">{t('golive.hideExplain')}</p>
            <label className="block">
              <span className="mb-1 block text-[12px] font-medium text-raad-muted">
                {t('golive.hideNote')}
              </span>
              <input
                className={INPUT}
                value={hideNote}
                onChange={(e) => setHideNote(e.target.value)}
              />
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void pressHide()}
                disabled={hide.isPending}
                className="min-h-10 rounded-lg bg-raad-warn px-4 text-[13px] font-semibold text-raad-onprimary disabled:opacity-40"
              >
                {hide.isPending ? t('inventory.saving') : t('golive.hideConfirm')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirmingHide(false);
                  setHideNote('');
                }}
                className={SECONDARY_BUTTON}
              >
                {t('golive.hideCancel')}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <button
              type="button"
              onClick={() => {
                setErrorKey(null);
                setFlash(null);
                setConfirmingHide(true);
              }}
              className={SECONDARY_BUTTON}
            >
              {t('golive.hide')}
            </button>
          </div>
        )}
      </div>
    );
  }

  function runChecks() {
    setErrorKey(null);
    setServerChecks(null);
    checks.mutate(vehicleId);
  }

  async function press() {
    setErrorKey(null);
    try {
      const res = await goLive.mutateAsync(vehicleId);
      setServerChecks(null);
      setOverride({ liveAt: res.liveAt, hiddenAt: null });
      // 🔴 TWO DIFFERENT EVENTS, TWO DIFFERENT SENTENCES. "The customer can see
      // it AGAIN" is not the same news as "the customer can see it", and the
      // server is the only side that knows which happened — `liveAt` cannot say,
      // because it is never rewritten. `wasHidden` is that answer.
      setFlash(res.wasHidden ? 'nowVisibleAgain' : 'nowLive');
    } catch (e) {
      // 🔴 The server's failing checks REPLACE the screen's. See the header.
      const failing = failingChecksFrom(e);
      if (failing !== null) {
        setServerChecks(failing);
        setErrorKey('golive.serverRefused');
      } else {
        setErrorKey(inventoryErrorKey(e));
      }
    }
  }

  const shown = serverChecks ?? checks.data?.checks ?? null;
  // A server 409 is a FAILING verdict by definition, whatever the stale screen
  // said a moment ago.
  const allPassed = serverChecks === null && checks.data?.allPassed === true;
  const hasRun = shown !== null;

  return (
    <div className="grid gap-3">
      {/* 🔴 SHOWN WHENEVER THE VEHICLE IS NOT LIVE — not only before the checks
          run. Everything below the early return above is by definition the
          not-live case, so this is unconditional here and disappears the moment
          it goes live.

          It used to be gated on `!hasRun`, which meant that with `autoRun` it
          flashed and vanished before anyone could read it. Both callers
          compensated by rendering their OWN permanent banner — and that one
          never disappeared, so after a successful go-live the operator saw "the
          customer cannot see it yet" and "Live. The customer can now see this
          vehicle." on screen at the same time. Mahad hit exactly that on the
          first real assignment, 31 Aug.

          One component owns the go-live state and everything said about it. */}
      {flashNote}

      {/* 🔴 THE HIDDEN CASE SAYS SOMETHING DIFFERENT, and the difference matters
          to the operator: "never cleared" and "cleared, then withdrawn" are two
          different histories, and only the second one has a date to answer "since
          when could the customer not see it". */}
      {isHidden ? (
        <>
          <p className="rounded-lg border border-raad-warn/40 bg-raad-warn/10 p-3 text-[12.5px]">
            {t('golive.hiddenBanner')}
          </p>
          <p className="text-[12.5px] text-raad-muted">
            {t('golive.liveSince', { when: formatInstant(eff.liveAt as string, i18n.language) })}
            {' · '}
            {t('golive.hiddenSince', {
              when: formatInstant(eff.hiddenAt as string, i18n.language),
            })}
          </p>
        </>
      ) : (
        <p className="rounded-lg border border-raad-warn/40 bg-raad-warn/10 p-3 text-[12.5px]">
          {t('golive.notLive')}
        </p>
      )}

      {checks.isPending && !hasRun ? (
        <p className="text-[13px] text-raad-muted">{t('provision.checking')}</p>
      ) : null}

      {shown !== null ? (
        <ul className="rounded-xl border border-raad-border px-3">
          {shown.map((c) => (
            <CheckRow key={c.id} check={c} />
          ))}
        </ul>
      ) : null}

      {checks.isError ? (
        <p role="alert" className="text-[12.5px] text-raad-error">
          {t('golive.checksError')}
        </p>
      ) : null}

      {errorKey !== null ? (
        <p
          role="alert"
          className="rounded-lg border border-raad-error/40 bg-raad-error/10 p-3 text-[12.5px] text-raad-text"
        >
          {t(errorKey)}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={runChecks}
          disabled={checks.isPending}
          className="min-h-10 rounded-lg border border-raad-border px-4 text-[13px] font-semibold transition-colors hover:bg-raad-primary/10 disabled:opacity-40"
        >
          {checks.isPending
            ? t('provision.checking')
            : hasRun
              ? t('provision.recheck')
              : t('golive.runChecks')}
        </button>
        {/* Disabled, deliberately, with the reason printed above it. See the
            header — this is an unmet precondition on a feature that exists, not
            a promise of one that does not. ABSENT for an `admin_staff`, whose
            click could only ever be a 403. */}
        {isSuperAdmin ? (
          <button
            type="button"
            onClick={() => void press()}
            disabled={!allPassed || goLive.isPending}
            className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-raad-onprimary disabled:opacity-40"
          >
            {isHidden ? t('golive.showAgain') : t('provision.goLive')}
          </button>
        ) : (
          <span className="self-center text-[12px] text-raad-muted">
            {t('golive.superAdminOnly')}
          </span>
        )}
      </div>
    </div>
  );
}
